export type PermissionLevel = 'READ' | 'FULL' | 'DELETE';
export type GrantCondition = 'ALWAYS' | 'OWNING' | 'COVERING' | 'CLIENT';

export interface ResourceEntry {
  key: string;
  name: string;
  /** What Read lets someone do, then what Full adds. */
  read: string;
  full: string;
  /** Only `case` takes a level above Full. */
  delete?: string;
  /** Case keys may be granted only on cases someone owns or covers. */
  perCase?: boolean;
}

export interface ResourceGroup {
  title: string;
  resources: readonly ResourceEntry[];
}

/** Every brokerage permission Keystone knows, grouped and worded for the grid. */
export const RESOURCE_GROUPS: readonly ResourceGroup[] = [
  {
    title: 'People and access',
    resources: [
      {
        key: 'members',
        name: 'Team members',
        read: 'See who is in the brokerage and their role',
        full: 'Invite people, change roles and remove people',
      },
      {
        key: 'access_control',
        name: 'Roles and permissions',
        read: 'See the roles and what each allows',
        full: 'Create roles and change what they allow',
      },
    ],
  },
  {
    title: 'Brokerage settings',
    resources: [
      {
        key: 'organisation_config',
        name: 'Branding and case setup',
        read: 'See branding, case stages, sources and task types',
        full: 'Change branding, case stages, sources and task types',
      },
      {
        key: 'organisation_settings',
        name: 'Brokerage settings',
        read: 'See the brokerage settings page',
        full: 'Change the brokerage settings',
      },
      {
        key: 'acquire_config',
        name: 'Acquire settings',
        read: 'See lead capture settings',
        full: 'Change lead capture settings',
      },
      {
        key: 'apply_config',
        name: 'Apply settings',
        read: 'See application form settings',
        full: 'Change application form settings',
      },
      {
        key: 'engage_config',
        name: 'Engage settings',
        read: 'See client messaging settings',
        full: 'Change client messaging settings',
      },
      {
        key: 'referral_links',
        name: 'Referral links',
        read: 'See referral links',
        full: 'Create and change referral links',
      },
      {
        key: 'brokerage_documents',
        name: 'Brokerage documents',
        read: 'See the brokerage document library',
        full: 'Upload and change brokerage documents',
      },
      {
        key: 'saved_filters',
        name: 'Saved filters',
        read: 'Use saved filters',
        full: 'Create and change saved filters',
      },
      {
        key: 'products',
        name: 'Products',
        read: 'See the product list',
        full: 'Change the product list',
      },
      {
        key: 'migrations',
        name: 'Data imports',
        read: 'See data imports',
        full: 'Run data imports',
      },
    ],
  },
  {
    title: 'Cases',
    resources: [
      {
        key: 'case',
        name: 'Cases',
        read: 'Open cases',
        full: 'Create and edit cases',
        delete: 'Delete cases',
        perCase: true,
      },
      {
        key: 'case_assignment',
        name: 'Case owner',
        read: 'See who owns a case',
        full: 'Change who owns a case',
        perCase: true,
      },
      {
        key: 'case_participants',
        name: 'People on a case',
        read: 'See who is on a case',
        full: 'Add and remove people on a case',
        perCase: true,
      },
      {
        key: 'case_invitations',
        name: 'Case invitations',
        read: 'See invitations to a case',
        full: 'Invite clients to a case',
        perCase: true,
      },
      {
        key: 'case_communications',
        name: 'Messages',
        read: 'Read case messages',
        full: 'Send case messages',
        perCase: true,
      },
      {
        key: 'case_uploads',
        name: 'Uploads',
        read: 'See uploaded documents',
        full: 'Upload, sort and request documents',
        perCase: true,
      },
      {
        key: 'case_files',
        name: 'Files',
        read: 'See case files',
        full: 'Manage case files',
        perCase: true,
      },
      {
        key: 'case_documents',
        name: 'Documents screen',
        read: 'See the documents screen',
        full: 'Manage the documents screen',
        perCase: true,
      },
      {
        key: 'case_generated_documents',
        name: 'Generated documents',
        read: 'See generated documents',
        full: 'Manage generated documents',
        perCase: true,
      },
      {
        key: 'case_document_generation',
        name: 'Document generator',
        read: 'Open the document generator',
        full: 'Generate documents',
        perCase: true,
      },
      {
        key: 'case_document_resources',
        name: 'Cover notes and fact finds',
        read: 'See cover notes, fact finds and packages',
        full: 'Write cover notes, fact finds and packages',
        perCase: true,
      },
      {
        key: 'case_quotes',
        name: 'Quotes',
        read: 'See quotes',
        full: 'Create and change quotes',
        perCase: true,
      },
      {
        key: 'case_suitability',
        name: 'Suitability statements',
        read: 'See suitability statements',
        full: 'Write suitability statements',
        perCase: true,
      },
      {
        key: 'case_products',
        name: 'Product finder',
        read: 'See product searches',
        full: 'Search and choose products',
        perCase: true,
      },
      {
        key: 'case_shared_documents',
        name: 'Shared documents',
        read: 'See documents shared with the client',
        full: 'Change documents shared with the client',
        perCase: true,
      },
    ],
  },
];

export const RESOURCES: readonly ResourceEntry[] = RESOURCE_GROUPS.flatMap((g) => g.resources);

export const CONDITION_NAMES: Record<GrantCondition, string> = {
  ALWAYS: 'Every case',
  OWNING: 'Cases they own',
  COVERING: 'Cases they cover',
  CLIENT: 'Their own case',
};

export const LEVEL_NAMES: Record<PermissionLevel, string> = {
  READ: 'View',
  FULL: 'Edit',
  DELETE: 'Delete',
};

const RANK: Record<PermissionLevel, number> = { READ: 1, FULL: 2, DELETE: 3 };

export interface Grant {
  resource: string;
  level: PermissionLevel;
  condition?: GrantCondition | null;
}

/** The highest level a role holds on a resource under one condition, or null. */
export function levelFor(
  grants: readonly Grant[],
  resource: string,
  condition: GrantCondition = 'ALWAYS',
): PermissionLevel | null {
  let best: PermissionLevel | null = null;
  for (const grant of grants) {
    if (grant.resource !== resource || (grant.condition ?? 'ALWAYS') !== condition) {
      continue;
    }
    if (!best || RANK[grant.level] > RANK[best]) {
      best = grant.level;
    }
  }
  return best;
}

/** One sentence for what a level allows, e.g. "Edit: Create and edit cases". */
export function describeLevel(entry: ResourceEntry, level: PermissionLevel): string {
  if (level === 'DELETE') {
    return entry.delete ?? entry.full;
  }
  return level === 'FULL' ? entry.full : entry.read;
}

/** Replace one resource/condition's level in a grant list; null removes it. */
export function withLevel(
  grants: readonly Grant[],
  resource: string,
  condition: GrantCondition,
  level: PermissionLevel | null,
): Grant[] {
  const kept = grants.filter(
    (g) => !(g.resource === resource && (g.condition ?? 'ALWAYS') === condition),
  );
  return level ? [...kept, { resource, level, condition }] : kept;
}

/** The friendly name of a resource key, or the key itself if unknown. */
export function resourceName(key: string): string {
  return RESOURCES.find((entry) => entry.key === key)?.name ?? key;
}

/** What a staff portfolio may grant on a brokerage, and the most it may grant. */
export const PORTFOLIO_GRANTABLE: readonly { key: string; max: PermissionLevel }[] = [
  { key: 'members', max: 'FULL' },
  { key: 'access_control', max: 'FULL' },
  { key: 'organisation_settings', max: 'FULL' },
  { key: 'organisation_config', max: 'FULL' },
  { key: 'acquire_config', max: 'FULL' },
  { key: 'apply_config', max: 'FULL' },
  { key: 'engage_config', max: 'FULL' },
];

export interface CellPart {
  level: PermissionLevel;
  condition: GrantCondition;
  /** e.g. "Edit" or "Edit on cases they own". */
  text: string;
}

const CONDITION_PHRASES: Record<Exclude<GrantCondition, 'ALWAYS'>, string> = {
  OWNING: 'on cases they own',
  COVERING: 'on cases they cover',
  CLIENT: 'on their own case',
};

/** What one role may do with one resource, broadest first; empty means no access. */
export function cellParts(grants: readonly Grant[], resource: string): CellPart[] {
  const parts: CellPart[] = [];
  const base = levelFor(grants, resource, 'ALWAYS');
  if (base) {
    parts.push({ level: base, condition: 'ALWAYS', text: LEVEL_NAMES[base] });
  }
  for (const condition of ['OWNING', 'COVERING', 'CLIENT'] as const) {
    const level = levelFor(grants, resource, condition);
    if (level && (!base || RANK[level] > RANK[base])) {
      parts.push({
        level,
        condition,
        text: `${LEVEL_NAMES[level]} ${CONDITION_PHRASES[condition]}`,
      });
    }
  }
  return parts;
}

/** The levels a resource takes, lowest first. */
export function levelsOf(entry: ResourceEntry): PermissionLevel[] {
  return entry.delete ? ['READ', 'FULL', 'DELETE'] : ['READ', 'FULL'];
}

export type PortfolioAccess = 'NONE' | 'VIEW' | 'MANAGE' | 'CUSTOM';

export const PORTFOLIO_ACCESS_NAMES: Record<PortfolioAccess, string> = {
  NONE: 'Not managed',
  VIEW: 'View only',
  MANAGE: 'Manage everything',
  CUSTOM: 'Custom (keep as it is)',
};

/** The grants a portfolio preset writes. */
export function portfolioGrants(
  access: 'VIEW' | 'MANAGE',
): { resource: string; level: PermissionLevel }[] {
  return PORTFOLIO_GRANTABLE.map(({ key, max }) => ({
    resource: key,
    level: access === 'VIEW' ? 'READ' : max,
  }));
}

/** Which preset a manager's grants match, or CUSTOM. */
export function portfolioAccessOf(
  grants: readonly { resource: string; level: PermissionLevel }[] | null,
): PortfolioAccess {
  if (grants === null) {
    return 'NONE';
  }
  const same = (wanted: { resource: string; level: PermissionLevel }[]) =>
    wanted.length === grants.length &&
    wanted.every((w) => grants.some((g) => g.resource === w.resource && g.level === w.level));
  if (same(portfolioGrants('MANAGE'))) {
    return 'MANAGE';
  }
  return same(portfolioGrants('VIEW')) ? 'VIEW' : 'CUSTOM';
}
